import { useEffect, useRef, useState } from "react";
import { Mic, Trash2 } from "lucide-react";
import { toast } from "sonner";

const MIME = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";

export function VoiceRecorder({ onDone, maxSeconds = 180 }) {
  const [rec, setRec] = useState(null);
  const [secs, setSecs] = useState(0);
  const [cancelZone, setCancelZone] = useState(false);
  const st = useRef({});

  const stop = (cancel) => {
    const s = st.current;
    if (!s.recorder) return;
    s.cancel = cancel;
    clearInterval(s.timer);
    cancelAnimationFrame(s.raf);
    if (s.recorder.state !== "inactive") s.recorder.stop();
    s.stream.getTracks().forEach((t) => t.stop());
    s.ctx?.close();
    setRec(null);
    setCancelZone(false);
  };

  useEffect(() => () => stop(true), []);

  const start = async (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (!window.MediaRecorder) {
      toast.error("L'enregistrement vocal n'est pas disponible sur ce navigateur.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const recorder = new MediaRecorder(stream, MIME ? { mimeType: MIME, audioBitsPerSecond: 24000 } : undefined);
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const levels = [];
      const chunks = [];
      const startX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
      st.current = { recorder, stream, ctx, startX, cancel: false, t0: Date.now() };
      const sample = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += ((v - 128) / 128) ** 2;
        levels.push(Math.sqrt(sum / buf.length));
        st.current.raf = requestAnimationFrame(sample);
      };
      sample();
      recorder.ondataavailable = (ev) => ev.data.size && chunks.push(ev.data);
      recorder.onstop = () => {
        const duration = (Date.now() - st.current.t0) / 1000;
        if (st.current.cancel || duration < 0.8) return;
        const n = 40;
        const step = Math.max(1, Math.floor(levels.length / n));
        const raw = Array.from({ length: Math.min(n, levels.length) }, (_, i) => Math.max(...levels.slice(i * step, (i + 1) * step), 0));
        const peak = Math.max(...raw, 0.01);
        const type = recorder.mimeType || MIME || "audio/webm";
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        onDone(new File(chunks, `note-vocale.${ext}`, { type }), { duration: Math.round(duration), waveform: raw.map((v) => +(v / peak).toFixed(2)) });
      };
      recorder.start(1000);
      setSecs(0);
      st.current.timer = setInterval(() => setSecs((s) => {
        if (s + 1 >= maxSeconds) stop(false);
        return s + 1;
      }), 1000);
      setRec(true);
    } catch {
      toast.error("Autorisez le micro pour enregistrer une note vocale.");
    }
  };

  const move = (e) => {
    if (!rec) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const inZone = st.current.startX - x > 90;
    setCancelZone(inZone);
    if (st.current.startX - x > 140) stop(true);
  };

  return (
    <div className="flex items-center gap-2 select-none">
      {rec && (
        <div data-testid="voice-recording" className={`flex items-center gap-2 text-sm px-3 h-11 rounded-full border ${cancelZone ? "border-mr-danger text-mr-danger" : "border-mr-border"}`}>
          <span className="w-2 h-2 rounded-full bg-mr-danger animate-pulse" />
          <span className="tabular">{Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}</span>
          <span className="text-mr-muted text-xs flex items-center gap-1"><Trash2 size={12} /> {cancelZone ? "Relâchez pour annuler" : "Glissez à gauche pour annuler"}</span>
        </div>
      )}
      <button type="button" data-testid="voice-record-button" aria-label="Maintenir pour enregistrer"
        onPointerDown={start} onPointerMove={move} onPointerUp={() => stop(cancelZone)} onPointerCancel={() => stop(true)} onContextMenu={(e) => e.preventDefault()}
        className={`w-11 h-11 shrink-0 rounded-full grid place-items-center touch-none ${rec ? "bg-mr-danger text-white scale-110" : "mr-btn-primary px-0"} transition-transform`}>
        <Mic size={18} />
      </button>
    </div>
  );
}
